import React, { useState, useEffect, useCallback, useMemo, } from 'react';
import {
    collection,
    doc,
    getDocs,
    getDoc,
    query,
    where,
    addDoc,
    updateDoc,
    writeBatch,
    serverTimestamp,
    limit,
    orderBy
} from 'firebase/firestore';
import { FileText, Loader2, Save, Users, User, Download, Printer, AlertTriangle, CheckCircle, BarChart, FileSpreadsheet } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';

// --- COMPOSANT DRAPEAU DU SÉNÉGAL ---
const SenegalFlag = () => (
    <div className="flex h-16 w-24 border border-gray-300 shadow-sm">
        <div className="bg-green-600 w-1/3 h-full"></div>
        <div className="bg-yellow-400 w-1/3 h-full flex items-center justify-center relative">
            <span className="text-green-600 text-2xl font-bold absolute" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -55%)' }}>★</span>
        </div>
        <div className="bg-red-600 w-1/3 h-full"></div>
    </div>
);

// --- COMPOSANT MESSAGE FLASH ---
const FlashMessage = ({ type, text, onClose }) => (
    <div className={`p-3 mb-4 rounded-lg flex items-center justify-between shadow-md transition-all duration-300 ${
        type === 'error' ? 'bg-red-100 border border-red-400 text-red-700' : 'bg-green-100 border border-green-400 text-green-700'
    }`}>
        <div className="flex items-center">
            {type === 'error' ? <AlertTriangle size={20} className="mr-2" /> : <CheckCircle size={20} className="mr-2" />}
            <span>{text}</span>
        </div>
        {onClose && (
            <button onClick={onClose} className="ml-4 text-gray-500 hover:text-gray-700">✕</button>
        )}
    </div>
);

// --- COMPOSANT D'AFFICHAGE DU BULLETIN ---
const BulletinDisplay = ({ bulletin, appreciationText, setAppreciationText, id, isPreview = false, bulletinType = 'tous' }) => {
    const formatDisplayData = (data) => {
        if (!data) return "";
        if (typeof data === 'object' && data.seconds !== undefined) {
            return new Date(data.seconds * 1000).toLocaleDateString('fr-FR');
        }
        return data;
    };

    const generateShortAppreciation = (moyenne20) => {
        if (moyenne20 === null || isNaN(moyenne20)) return "-";
        if (moyenne20 >= 16) return "Très Bien";
        else if (moyenne20 >= 14) return "Bien";
        else if (moyenne20 >= 12) return "Assez Bien";
        else if (moyenne20 >= 10) return "Passable";
        else if (moyenne20 >= 8) return "Insuffisant";
        else return "Très Insuffisant";
    };

    const baseNotation = bulletin.baseNotation || (bulletin.isPrimaire ? 10 : 20);

    const scaleScore = (score) => {
        if (score === 'ABS' || score === 'N/A' || score === '-' || score === null || typeof score === 'string') return score;
        if (baseNotation === 20) return score;
        // Convertir de 20 à 10 si nécessaire
        if (score > 10 && baseNotation === 10) {
            return parseFloat((score / 2).toFixed(2));
        }
        return parseFloat(score.toFixed(2));
    };

    const scalePreviousAverage = (v) => {
        if (v === null || isNaN(v)) return null;
        if (baseNotation === 20) return v;
        return parseFloat((v / 2).toFixed(2));
    };

    const scaledMoyenneGenerale = scaleScore(bulletin.moyenneGenerale);
    const scaledMoyenneClasse = scaleScore(bulletin.moyenneClasse);
    
    // Pour primaire, toujours afficher l'historique si des données existent
    const shouldDisplayHistoryBlock = bulletin.isPrimaire ? 
        (bulletin.moyenne1erTrimestre !== null || bulletin.moyenne2eTrimestre !== null || bulletin.moyenne3eTrimestre !== null) :
        ((bulletin.isPrimaire && (bulletin.periode === '2ème Trimestre' || bulletin.periode === '3ème Trimestre')) ||
         (bulletin.isSecondaire && bulletin.periode === 'Semestre 2'));

    // Calculer la moyenne annuelle pour primaire (seulement au 3ème trimestre)
    const calculateMoyenneAnnuelle = () => {
        if (!bulletin.isPrimaire) return bulletin.moyenneAnnuelle;
        
        // Pour primaire, calculer la moyenne annuelle seulement au 3ème trimestre
        if (bulletin.periode !== '3ème Trimestre') {
            return null;
        }
        
        const trimestres = [bulletin.moyenne1erTrimestre, bulletin.moyenne2eTrimestre, bulletin.moyenne3eTrimestre];
        const trimestresValides = trimestres.filter(t => t !== null && !isNaN(t));
        
        if (trimestresValides.length === 0) return null;
        
        const somme = trimestresValides.reduce((acc, curr) => acc + curr, 0);
        return parseFloat((somme / trimestresValides.length).toFixed(2));
    };

    const moyenneAnnuelle = calculateMoyenneAnnuelle();
    const scaledMoyenneAnnuelle = scaleScore(moyenneAnnuelle);

    // Filtrer les matières selon le type de bulletin
    const filteredDetails = bulletinType === 'tous' 
        ? bulletin.detailsParMatiere
        : bulletin.detailsParMatiere.filter(item => {
            if (bulletinType === 'français') {
                return item.matiereType === 'français' || item.matiereType === 'commun';
            } else if (bulletinType === 'arabe') {
                return item.matiereType === 'arabe' || item.matiereType === 'commun';
            }
            return true;
        });

    // Titre du bulletin selon le type
    const getBulletinTitle = () => {
        if (bulletinType === 'français') {
            return `${bulletin.isPrimaire ? 'FICHE D\'ÉVALUATION' : 'BULLETIN'} DU ${bulletin.periode.toUpperCase()} - MATIÈRES FRANÇAISES`;
        } else if (bulletinType === 'arabe') {
            return `${bulletin.isPrimaire ? 'FICHE D\'ÉVALUATION' : 'BULLETIN'} DU ${bulletin.periode.toUpperCase()} - MATIÈRES ARABES`;
        } else {
            return `${bulletin.isPrimaire ? 'FICHE D\'ÉVALUATION' : 'BULLETIN'} DU ${bulletin.periode.toUpperCase()}`;
        }
    };

    return (
        <div 
            id={id || "bulletin-to-print"} 
            className="bulletin-wrapper p-5 border-2 border-gray-400 rounded-lg shadow-xl bg-white max-w-5xl mx-auto mb-8"
            style={isPreview ? { transform: 'scale(0.75)', transformOrigin: 'top left', width: '133.33%', margin: '0 auto 2rem auto', overflow: 'visible' } : {}}
        >
            {/* EN-TÊTE */}
            <div className="flex justify-between items-center border-b-2 border-black pb-3 mb-4">
                <div className="w-1/4"><SenegalFlag /></div>
                <div className="w-1/2 text-center leading-tight">
                    <p className="text-xl font-extrabold text-blue-700">RÉPUBLIQUE DU SÉNÉGAL</p>
                    <p className="text-2xl font-bold mt-2 text-gray-800">MINISTÈRE DE L'ÉDUCATION NATIONALE</p>
                    {bulletinType !== 'tous' && (
                        <p className="text-lg font-bold mt-1 text-red-600">
                            {bulletinType === 'français' ? 'SECTION FRANÇAISE' : 'SECTION ARABE'}
                        </p>
                    )}
                </div>
                <div className="w-1/4 flex justify-end">
                    {bulletin.schoolInfo?.logoBase64 || bulletin.schoolInfo?.logoUrl ? (
                        <img src={bulletin.schoolInfo.logoBase64 || bulletin.schoolInfo.logoUrl} className="h-16 w-auto" alt="logo" crossOrigin="anonymous" />
                    ) : (
                        <div className="h-16 w-16 bg-gray-200 flex items-center justify-center text-xs">Logo</div>
                    )}
                </div>
            </div>

            {/* INFO ÉCOLE */}
            <div className="text-center mb-6 p-2 border-2 border-blue-700 bg-blue-50">
                <p className="text-3xl font-extrabold uppercase text-blue-800">{bulletin.schoolInfo?.name || "NOM ÉCOLE MANQUANT"}</p>
                <p className="text-xl text-gray-800">Année Scolaire : <b>{bulletin.anneeScolaire}</b></p>
                <p className="text-lg text-gray-600">
                    {formatDisplayData(bulletin.schoolInfo?.adresse_ecole)}
                    {bulletin.schoolInfo?.telephone_ecole ? ` - Tél: ${bulletin.schoolInfo.telephone_ecole}` : ''}
                </p>
            </div>

            <h1 className="text-4xl font-black text-center uppercase my-6 p-4 bg-blue-700 text-white shadow-lg border-b border-black">
                {getBulletinTitle()}
                {bulletin.isPrimaire && bulletin.periode === '3ème Trimestre' && moyenneAnnuelle && (
                    <span className="block text-2xl font-bold mt-2 text-yellow-300">
                        MOYENNE ANNUELLE: {scaledMoyenneAnnuelle} / {baseNotation}
                    </span>
                )}
            </h1>
        
            {/* INFO ÉLÈVE */}
            <div className="text-xl border-2 border-black p-2 mb-4 grid grid-cols-2 gap-y-1">
                <p className="font-semibold text-gray-900">Niveau/Classe: {bulletin.niveau} / {bulletin.classeNom}</p>
                <p className="font-semibold text-gray-900">Effectif: {bulletin.totalStudentsInClass}</p>
                <p className="font-semibold text-gray-900">Elève: {bulletin.eleveNomComplet}</p>
                <p className="font-semibold text-gray-900">Né(e) le: {formatDisplayData(bulletin.eleveDetails?.dateDeNaissance)} à {bulletin.eleveDetails?.placeOfBirth}</p>
            </div>

            {/* ASSIDUITÉ */}
            {bulletin.absencesRetardsSummary && (
                <div className="mb-4 p-1 border-2 border-orange-500 bg-orange-50">
                    <p className="font-bold mb-2 text-xl uppercase text-orange-800">ASSIDUITÉ ({bulletin.anneeScolaire})</p>
                    <div className="grid grid-cols-2 gap-4 text-xl">
                        <p>Total Absences: <b>{bulletin.absencesRetardsSummary.totalAbsences}</b></p>
                        <p>Total Retards: <b>{bulletin.absencesRetardsSummary.totalRetards}</b></p>
                    </div>
                </div>
            )}
            
            {/* TABLEAU DES NOTES - ADAPTÉ POUR PRIMAIRE */}
            <div className="overflow-x-auto mb-8">
                <table className="min-w-full border-2 border-black divide-y divide-gray-200 text-lg">
                    <thead className="bg-gray-200">
                        <tr>
                            <th className="border border-black p-1 align-middle font-extrabold text-xl">Matière</th>
                            {bulletinType !== 'tous' && (
                                <th className="border border-black p-1 align-middle font-extrabold text-xl">Type</th>
                            )}
                            
                            {bulletin.isPrimaire ? (
                                // En-tête pour primaire: afficher les 3 trimestres
                                <>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Trim 1</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Trim 2</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Trim 3</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Moy/ {baseNotation}</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Appréciation</th>
                                </>
                            ) : (
                                // En-tête pour secondaire (existant)
                                <>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Dev1</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Dev2</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Moy.Dev</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Compo</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Moy/ {baseNotation}</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Coef</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Pts</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Rang</th>
                                    <th className="border border-black p-1 align-middle font-extrabold text-xl">Appréciation</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredDetails.map((item, index) => (
                            <tr key={item.matiere} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="border border-black p-1 font-extrabold text-center align-middle text-1xl font-bold">
                                    {item.matiere}
                                </td>
                                
                                {bulletinType !== 'tous' && (
                                    <td className="border border-black p-1 text-center align-middle">
                                        <span className={`px-2 py-1 text-xs rounded ${
                                            item.matiereType === 'arabe' ? 'bg-green-100 text-green-800' :
                                            item.matiereType === 'commun' ? 'bg-purple-100 text-purple-800' :
                                            'bg-blue-100 text-blue-800'
                                        }`}>
                                            {item.matiereType === 'arabe' ? 'Arabe' : 
                                             item.matiereType === 'commun' ? 'Commun' : 'Français'}
                                        </span>
                                    </td>
                                )}
                                
                                {bulletin.isPrimaire ? (
                                    // Affichage pour primaire: 3 trimestres
                                    <>
                                        <td className="border border-black p-1 text-center align-middle text-2xl font-bold">
                                            {scaleScore(item.notesTrimestres?.['1er Trimestre'] || '-')}
                                        </td>
                                        <td className="border border-black p-1 text-center align-middle text-2xl font-bold">
                                            {scaleScore(item.notesTrimestres?.['2ème Trimestre'] || '-')}
                                        </td>
                                        <td className="border border-black p-1 text-center align-middle text-2xl font-bold">
                                            {scaleScore(item.notesTrimestres?.['3ème Trimestre'] || '-')}
                                        </td>
                                        <td className="border border-black p-1 text-center font-extrabold align-middle text-2xl">
                                            {scaleScore(item.moyenneMatiere)}
                                        </td>
                                        <td className="border border-black p-1 text-center font-bold align-middle text-2xl">
                                            {generateShortAppreciation(item.moyenneMatiere)}
                                        </td>
                                    </>
                                ) : (
                                    // Affichage pour secondaire (existant)
                                    <>
                                        <td className="border border-black p-1 text-center align-middle text-2xl font-bold">
                                            {scaleScore(item.note1)}
                                        </td>
                                        <td className="border border-black p-1 text-center align-middle text-2xl font-bold">
                                            {scaleScore(item.note2)}
                                        </td>
                                        <td className="border border-black p-1 text-center font-extrabold align-middle text-2xl font-bold">
                                            {scaleScore(item.moyDevoirs)}
                                        </td>
                                        <td className="border border-black p-1 text-center font-medium align-middle text-2xl font-bold">
                                            {scaleScore(item.noteCompo)}
                                        </td>
                                        <td className="border border-black p-1 text-center font-extrabold align-middle text-2xl">
                                            {scaleScore(item.moyenneMatiere)}
                                        </td>
                                        <td className="border border-black p-1 text-center font-extrabold align-middle text-2xl">
                                            {item.coefficient}
                                        </td>
                                        <td className="border border-black p-1 text-center font-extrabold align-middle text-2xl">
                                            {scaleScore(item.totalPointsMatiere)}
                                        </td>
                                        <td className="border border-black p-1 text-center font-bold align-middle text-2xl">
                                            {item.rankMatiere}
                                        </td>
                                        <td className="border border-black p-1 text-center font-bold align-middle text-2xl">
                                            {generateShortAppreciation(item.moyenneMatiere)}
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                        
                        {!bulletin.isPrimaire && (
                            <tr className="bg-gray-200 font-extrabold text-2xl">
                                <td colSpan={bulletinType !== 'tous' ? "7" : "6"} className="border border-black p-1 text-right uppercase align-middle">
                                    Totaux :
                                </td>
                                <td className="border border-black p-1 text-center font-bold align-middle text-2xl">
                                    {filteredDetails.reduce((sum, item) => sum + (item.coefficient || 0), 0)}
                                </td>
                                <td className="border border-black p-1 text-center align-middle">
                                    {scaleScore(filteredDetails.reduce((sum, item) => sum + (item.totalPointsMatiere || 0), 0))}
                                </td>
                                <td colSpan="2" className="border border-black align-middle"></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* RÉSUMÉ - ADAPTÉ POUR PRIMAIRE */}
            <div className={`grid gap-6 mb-8 text-2xl ${shouldDisplayHistoryBlock || bulletin.isPrimaire ? 'grid-cols-4' : 'grid-cols-3'}`}>
                {/* 1. Moyenne Actuelle */}
                <div className="border-2 border-blue-700 rounded bg-blue-50 p-2 text-center flex flex-col justify-center">
                    <p className="font-bold text-gray-800">Moyenne {bulletin.periode}</p>
                    <p className="text-3xl font-extrabold text-blue-700">
                        {scaledMoyenneGenerale} / {baseNotation}
                    </p>
                </div>
                
                {/* 2. Historique des Trimestres (toujours visible pour primaire si données) */}
                {(shouldDisplayHistoryBlock || bulletin.isPrimaire) && (
                    <div className="border-2 border-gray-400 rounded bg-gray-50 p-2 text-center flex flex-col justify-center col-span-2">
                        <p className="font-bold text-gray-700 mb-2 uppercase text-lg">Historique des Trimestres</p>
                        <div className="flex justify-around items-center text-lg">
                            {bulletin.isPrimaire ? (
                                <>
                                    <div className="mx-1">
                                        <b>1er Trim:</b> {scaleScore(bulletin.moyenne1erTrimestre) || '-'}
                                    </div>
                                    <div className="mx-1">
                                        <b>2ème Trim:</b> {scaleScore(bulletin.moyenne2eTrimestre) || '-'}
                                    </div>
                                    <div className="mx-1">
                                        <b>3ème Trim:</b> {scaleScore(bulletin.moyenne3eTrimestre) || '-'}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {scalePreviousAverage(bulletin.moyenne1erTrimestre) !== null && <div className="mx-1"><b>1er:</b> {scalePreviousAverage(bulletin.moyenne1erTrimestre)}</div>}
                                    {scalePreviousAverage(bulletin.moyenne2eTrimestre) !== null && <div className="mx-1"><b>2e:</b> {scalePreviousAverage(bulletin.moyenne2eTrimestre)}</div>}
                                    {scalePreviousAverage(bulletin.moyenne3eTrimestre) !== null && <div className="mx-1"><b>3e:</b> {scalePreviousAverage(bulletin.moyenne3eTrimestre)}</div>}
                                    {scalePreviousAverage(bulletin.moyenneSemestre1) !== null && <div className="mx-1"><b>S1:</b> {scalePreviousAverage(bulletin.moyenneSemestre1)}</div>}
                                    {scalePreviousAverage(bulletin.moyenneSemestre2) !== null && <div className="mx-1"><b>S2:</b> {scalePreviousAverage(bulletin.moyenneSemestre2)}</div>}
                                </>
                            )}
                        </div>
                        
                        {/* Moyenne annuelle - Afficher uniquement pour primaire au 3ème trimestre */}
                        {bulletin.isPrimaire && bulletin.periode === '3ème Trimestre' && scaledMoyenneAnnuelle !== null && !isNaN(scaledMoyenneAnnuelle) && (
                            <div className="mt-1 pt-1 border-t border-gray-300">
                                <span className="font-bold text-green-700 text-lg">
                                    Moyenne Annuelle: {scaledMoyenneAnnuelle} / {baseNotation}
                                </span>
                            </div>
                        )}
                    </div>
                )}
                
                {/* 3. Rang */}
                <div className="border-2 border-blue-700 bg-blue-50 p-1 text-center flex flex-col justify-center">
                    <p className="font-bold text-gray-800">RANG</p>
                    <p className="text-3xl font-black text-blue-700">
                        {bulletin.rank} / {bulletin.totalStudentsInClass}
                    </p>
                </div>
                
                {/* 4. Moyenne Classe */}
                <div className="border-2 border-gray-400 p-1 text-center flex flex-col justify-center">
                    <p className="font-bold text-gray-800">Moy. Classe</p>
                    <p className="text-3xl font-black text-gray-700">
                        {scaledMoyenneClasse}
                    </p>
                </div>
            </div>

            <div className="mb-6 border-2 border-black p-1">
                <p className="font-bold mb-1 uppercase text-blue-800">Appréciation du conseil :</p>
                {id === 'bulletin-to-print' && setAppreciationText ? (
                    <textarea value={appreciationText} onChange={(e) => setAppreciationText(e.target.value)} className="w-full text-2xl min-h-[20px] border border-gray-300 p-2" />
                ) : (
                    <div className="text-xl min-h-[20px] whitespace-pre-wrap">{appreciationText || bulletin.appreciations}</div>
                )}
            </div>

            {/* SIGNATURES */}
            <div className="flex justify-between text-xl mt-4 pt-2">
                <p className="font-semibold">Fait à {formatDisplayData(bulletin.schoolInfo?.adresse_ecole) || "........"}, le {new Date().toLocaleDateString('fr-FR')}</p>
                <div>
                    <p className="font-semibold">Le Directeur/La Directrice,</p>
                    <div className="w-60 h-10 bg-gray-100 mt-1"></div>
                </div>
            </div>
        </div>
    );
};

// --- COMPOSANT PRINCIPAL BULLETINS MANAGEMENT ---
const BulletinsManagement = ({ db, setError, setMessage, schoolId, userRole, schoolInfo = {} }) => {
    const [eleves, setEleves] = useState([]);
    const [classes, setClasses] = useState([]);
    const [matieres, setMatieres] = useState([]);
    const [schoolSettings, setSchoolSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generatingBulletin, setGeneratingBulletin] = useState(false);
    const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0, message: '' });
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [viewMode, setViewMode] = useState('individual');
    const [statistics, setStatistics] = useState(null);
    const [selectedEleveId, setSelectedEleveId] = useState('');
    const [selectedClasseIdForBulk, setSelectedClasseIdForBulk] = useState('');
    const [bulkBulletins, setBulkBulletins] = useState([]);
    const [selectedPeriode, setSelectedPeriode] = useState('');
    const [selectedAnneeScolaire, setSelectedAnneeScolaire] = useState('');
    const [generatedBulletin, setGeneratedBulletin] = useState(null);
    const [frenchBulletin, setFrenchBulletin] = useState(null);
    const [arabicBulletin, setArabicBulletin] = useState(null);
    const [selectedBulletinType, setSelectedBulletinType] = useState('tous'); // 'tous', 'français', 'arabe'
    const [appreciationText, setAppreciationText] = useState('');
    const [arabicAppreciationText, setArabicAppreciationText] = useState('');
    const [frenchAppreciationText, setFrenchAppreciationText] = useState('');

    const periodeOptions = useMemo(() => [
        { value: '1er Trimestre', label: '1er Trimestre' },
        { value: '2ème Trimestre', label: '2ème Trimestre' },
        { value: '3ème Trimestre', label: '3ème Trimestre' },
        { value: 'Semestre 1', label: 'Semestre 1' },
        { value: 'Semestre 2', label: 'Semestre 2' },
    ], []);

    const anneeScolaireOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = -2; i <= 2; i++) {
            const year = currentYear + i;
            years.push({ value: `${year}-${year + 1}`, label: `${year}-${year + 1}` });
        }
        return years;
    }, []);

    const bulletinTypeOptions = [
        { value: 'tous', label: 'Toutes les matières' },
        { value: 'français', label: 'Matières Françaises' },
        { value: 'arabe', label: 'Matières Arabes' }
    ];

    // Fonction pour extraire dynamiquement les listes de matières par type
    const getSubjectLists = useMemo(() => {
        if (!matieres || matieres.length === 0) {
            return {
                arabicSubjects: [],
                commonSubjects: [],
                frenchSubjects: []
            };
        }

        // Extraire les noms de matières par type
        const arabicSubjects = matieres
            .filter(m => m.type === 'arabe')
            .map(m => m.name.toLowerCase());
        
        const frenchSubjects = matieres
            .filter(m => m.type === 'français')
            .map(m => m.name.toLowerCase());
        
        const commonSubjects = matieres
            .filter(m => m.type === 'commun')
            .map(m => m.name.toLowerCase());

        return {
            arabicSubjects,
            commonSubjects,
            frenchSubjects
        };
    }, [matieres]);

    // Fonction pour déterminer le type de matière à partir du nom (dynamique)
    const determineMatiereTypeFromName = useCallback((matiereName) => {
        if (!matiereName) return 'français';
        
        const nameLower = matiereName.toLowerCase();
        const { arabicSubjects, commonSubjects, frenchSubjects } = getSubjectLists;

        // Vérifier d'abord si c'est une matière arabe
        if (arabicSubjects.some(subject => nameLower.includes(subject.toLowerCase()))) {
            return 'arabe';
        }
        
        // Vérifier si c'est une matière commune
        if (commonSubjects.some(subject => nameLower.includes(subject.toLowerCase()))) {
            return 'commun';
        }
        
        // Vérifier si c'est une matière française spécifique
        if (frenchSubjects.some(subject => nameLower.includes(subject.toLowerCase()))) {
            return 'français';
        }
        
        // Liste de mots-clés par défaut pour déterminer le type
        const defaultArabicKeywords = [
            'arabe', 'coran', 'islamique', 'islam', 'fiqh', 'aqida', 
            'tawhid', 'hadith', 'sira', 'théologie', 'religion',
            'quran', 'dini', 'islamiyyah', 'تعليم إسلامي', 'ديني',
            'قرآن', 'تجويد', 'فقه', 'عقيدة', 'حديث', 'سيرة'
        ];
        
        const defaultCommonKeywords = [
            'mathématiques', 'maths', 'sciences', 'physique', 'chimie', 
            'histoire', 'géographie', 'informatique', 'sport', 'éducation physique',
            'arts', 'dessin', 'musique', 'biologie', 'géologie'
        ];
        
        if (defaultArabicKeywords.some(keyword => nameLower.includes(keyword))) {
            return 'arabe';
        }
        
        if (defaultCommonKeywords.some(keyword => nameLower.includes(keyword))) {
            return 'commun';
        }
        
        // Par défaut, c'est une matière française
        return 'français';
    }, [getSubjectLists]);

    // Fonction pour obtenir le type de matière d'une liaison (gère anciennes et nouvelles)
    const getMatiereTypeFromLiaison = useCallback((liaison) => {
        // 1. Si matiereType existe, l'utiliser
        if (liaison.matiereType) {
            const type = liaison.matiereType.toLowerCase().trim();
            // Normaliser les variations
            if (type === 'francais' || type === 'français') return 'français';
            if (type === 'arabe') return 'arabe';
            if (type === 'commun') return 'commun';
            return type;
        }
        
        // 2. Sinon déterminer à partir du nom (avec la fonction dynamique)
        return determineMatiereTypeFromName(liaison.matiereName);
    }, [determineMatiereTypeFromName]);

    useEffect(() => {
        if (!db || !schoolId) {
            if (!schoolId) setErrorMessage("Veuillez sélectionner une école.");
            else if (!db) setErrorMessage("Service de base de données non disponible.");
            setLoading(false);
            return;
        }

        const fetchInitialData = async () => {
            setLoading(true);
            setErrorMessage('');
            setSuccessMessage('');
            
            try {
                const [elevesSnap, classesSnap, matieresSnap] = await Promise.all([
                    getDocs(collection(db, 'schools', schoolId, 'eleves')),
                    getDocs(collection(db, 'schools', schoolId, 'classes')),
                    getDocs(collection(db, 'schools', schoolId, 'matieres_globales'))
                ]);

                setEleves(elevesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setClasses(classesSnap.docs.map(doc => ({ id: doc.id, name: doc.data().nom, baseNotation: doc.data().baseNotation, ...doc.data() })));
                setMatieres(matieresSnap.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name,
                    niveau: doc.data().niveau,
                    description: doc.data().description,
                    type: doc.data().type || 'français'
                })));

                const schoolDocRef = doc(db, 'schools', schoolId);
                const schoolDocSnap = await getDoc(schoolDocRef);
                if (schoolDocSnap.exists()) {
                    setSchoolSettings(schoolDocSnap.data());
                }

                setSuccessMessage('Données chargées.');
            } catch (err) {
                console.error("Erreur chargement global:", err);
                setErrorMessage("Erreur lors du chargement des données initiales.");
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [db, schoolId]);

    const urlToBase64 = useCallback((url) => {
        if (!url) return Promise.resolve(null);
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/jpeg', 0.9));
                } catch(e) {
                    console.error("Conversion to canvas failed.");
                    resolve(null);
                }
            };
            img.onerror = () => {
                console.warn(`[Logo] Échec du chargement de l'image. URL : ${url}`);
                resolve(null);
            };
            img.src = url;
        });
    }, []);

    const generateAppreciation = (moyenne20) => {
        if (moyenne20 >= 16) return "Excellent travail, très bons résultats !";
        else if (moyenne20 >= 14) return "Bon travail, de solides acquis. Bravo !";
        else if (moyenne20 >= 12) return "Travail satisfaisant, les bases sont là.";
        else if (moyenne20 >= 10) return "Résultats corrects, efforts à poursuivre.";
        else return "Des difficultés, un travail plus régulier est nécessaire.";
    };

    // FONCTION MODIFIÉE : Système dual primaire/secondaire avec support franco-arabe
    const calculateStudentWeightedAverageAndDetails = useCallback((studentId, studentClassId, period, anneeScolaire, allNotes, allMatieres, classMatieresLiaisons, niveau, bulletinType = 'tous') => {
        const niveauLower = (niveau || '').toLowerCase();
        const isPrimaire = niveauLower.includes('élémentaire') || 
                           niveauLower.includes('préscolaire') || 
                           niveauLower.includes('prescolaire') ||
                           niveauLower.includes('primaire') ||
                           niveauLower.includes('maternelle');
        
        // Filtrer les liaisons selon le type de bulletin
        let filteredLiaisons = classMatieresLiaisons;
        if (bulletinType !== 'tous') {
            filteredLiaisons = classMatieresLiaisons.filter(liaison => {
                const matiereType = getMatiereTypeFromLiaison(liaison);
                
                if (bulletinType === 'français') {
                    return matiereType === 'français' || matiereType === 'commun';
                } else if (bulletinType === 'arabe') {
                    return matiereType === 'arabe' || matiereType === 'commun';
                }
                return true;
            });
            
            console.log(`Filtrage ${bulletinType}: ${classMatieresLiaisons.length} -> ${filteredLiaisons.length} matières`);
        }
        
        const studentNotes = allNotes.filter(note =>
            String(note.eleveId) === String(studentId) &&
            String(note.classeId) === String(studentClassId) &&
            String(note.anneeScolaire) === String(anneeScolaire)
        );
        
        let totalWeightedScoreOverall = 0;
        let totalCoefficientsOverall = 0;
        const gradesByMatiereForStudent = {};

        studentNotes.forEach(note => {
            const matiereLiaison = filteredLiaisons.find(
                liaison => liaison.classId === studentClassId && liaison.matiereId === note.matiereId
            );
            if (!matiereLiaison) return;

            const typeEval = note.typeEvaluation?.toLowerCase() || '';
            const noteMax = parseFloat(matiereLiaison.noteSur) || (isPrimaire ? 10 : 20);
            const coefficient = parseFloat(matiereLiaison.coefficient) || 1;
            if (isNaN(coefficient)) return;

            if (!gradesByMatiereForStudent[note.matiereNom]) {
                const matiereType = getMatiereTypeFromLiaison(matiereLiaison);
                
                gradesByMatiereForStudent[note.matiereNom] = {
                    devoirs: [],
                    composition: null,
                    compositionCoefficient: 1,
                    devoirsCoefficient: 2,
                    coefficient: coefficient,
                    matiereId: note.matiereId,
                    noteMax: noteMax,
                    matiereType: matiereType,
                    notesParTrimestre: {}  // Pour stocker les notes par trimestre pour primaire
                };
            }

            // Pour primaire: une seule note par matière/trimestre
            if (isPrimaire) {
                let val = null;
                
                // Chercher la note dans différents champs possibles
                if (note.valeurNote !== undefined && note.valeurNote !== null) {
                    val = parseFloat(note.valeurNote);
                } else if (note.valeurNoteDevoir1 !== undefined && note.valeurNoteDevoir1 !== null) {
                    val = parseFloat(note.valeurNoteDevoir1);
                } else if (note.valeurNoteDevoir2 !== undefined && note.valeurNoteDevoir2 !== null) {
                    val = parseFloat(note.valeurNoteDevoir2);
                }
                
                if (val !== null && !isNaN(val)) {
                    // Stocker la note originale pour ce trimestre
                    gradesByMatiereForStudent[note.matiereNom].notesParTrimestre[note.trimestre] = val;
                    
                    // Si c'est le trimestre courant, utiliser cette note pour le calcul
                    if (note.trimestre === period) {
                        // Convertir en base 20 pour calculs internes (si note sur 10)
                        let notePourCalcul = val;
                        if (noteMax !== 20) {
                            notePourCalcul = (val / noteMax) * 20;
                        }
                        gradesByMatiereForStudent[note.matiereNom].notePourCalcul = notePourCalcul;
                    }
                }
            } else {
                // Système secondaire existant
                if (typeEval.includes('comp') || typeEval.includes('oral')) {
                    let val = parseFloat(note.valeurNote);
                    if (!isNaN(val)) {
                        if (noteMax !== 20) val = (val / noteMax) * 20;
                        gradesByMatiereForStudent[note.matiereNom].composition = val;
                    }
                } else if (typeEval.includes('devoir')) {
                    [note.valeurNoteDevoir1, note.valeurNoteDevoir2].forEach(val => {
                        let v = parseFloat(val);
                        if (!isNaN(v)) {
                            if (noteMax !== 20) v = (v / noteMax) * 20;
                            gradesByMatiereForStudent[note.matiereNom].devoirs.push(v);
                        }
                    });
                    if (note.valeurNote && !note.valeurNoteDevoir1 && !note.valeurNoteDevoir2) {
                        let old = parseFloat(note.valeurNote);
                        if (!isNaN(old)) {
                            if (noteMax !== 20) old = (old / noteMax) * 20;
                            gradesByMatiereForStudent[note.matiereNom].devoirs.push(old);
                        }
                    }
                }
            }
        });

        const matiereAveragesAndDetails = Object.keys(gradesByMatiereForStudent).map(matiereNom => {
            const data = gradesByMatiereForStudent[matiereNom];
            
            let finalMoyenneMatiere = 0;
            let note1 = 'N/A';
            let note2 = 'N/A';
            let moyDevoirs = 'N/A';
            let noteCompo = 'N/A';
            let notesTrimestres = data.notesParTrimestre || {};

            if (isPrimaire) {
                // Pour primaire: utiliser la note du trimestre courant
                const notePourCeTrimestre = data.notesParTrimestre[period];
                
                if (notePourCeTrimestre !== undefined && notePourCeTrimestre !== null) {
                    finalMoyenneMatiere = notePourCeTrimestre;
                    note1 = parseFloat(notePourCeTrimestre.toFixed(2));
                } else if (data.notePourCalcul !== undefined) {
                    // Convertir de base 20 à base d'affichage si nécessaire
                    finalMoyenneMatiere = data.notePourCalcul;
                    // Si la note est sur 20 mais qu'on affiche sur 10, convertir
                    if (data.noteMax === 20) {
                        note1 = parseFloat((data.notePourCalcul / 2).toFixed(2));
                    } else {
                        note1 = parseFloat(data.notePourCalcul.toFixed(2));
                    }
                } else {
                    note1 = 'N/A';
                    finalMoyenneMatiere = 0;
                }
            } else {
                // Système secondaire existant
                const totalDevoirs = data.devoirs.reduce((sum, n) => sum + n, 0);
                moyDevoirs = data.devoirs.length > 0 ? totalDevoirs / data.devoirs.length : 0;
                noteCompo = data.composition;
                
                note1 = data.devoirs[0] ? parseFloat(data.devoirs[0].toFixed(2)) : 'N/A';
                note2 = data.devoirs[1] ? parseFloat(data.devoirs[1].toFixed(2)) : 'N/A';
                
                if (noteCompo === null) finalMoyenneMatiere = moyDevoirs;
                else if (data.devoirs.length === 0) finalMoyenneMatiere = noteCompo;
                else finalMoyenneMatiere = ((moyDevoirs * data.devoirsCoefficient) + (noteCompo * data.compositionCoefficient)) / (data.devoirsCoefficient + data.compositionCoefficient);
            }

            const totalPointsMatiere = finalMoyenneMatiere * data.coefficient;
            totalWeightedScoreOverall += totalPointsMatiere;
            totalCoefficientsOverall += data.coefficient;

            return {
                matiere: matiereNom,
                coefficient: data.coefficient,
                note1: note1,
                note2: note2,
                moyDevoirs: moyDevoirs !== 'N/A' ? parseFloat(moyDevoirs.toFixed(2)) : moyDevoirs,
                noteCompo: noteCompo !== 'N/A' && noteCompo !== null ? parseFloat(noteCompo.toFixed(2)) : noteCompo,
                moyenneMatiere: parseFloat(finalMoyenneMatiere.toFixed(2)),
                totalPointsMatiere: parseFloat(totalPointsMatiere.toFixed(2)),
                notesTrimestres: notesTrimestres,
                isPrimaire: isPrimaire,
                noteMax: data.noteMax || (isPrimaire ? 10 : 20),
                matiereType: data.matiereType
            };
        });

        const overallWeightedAverage = totalCoefficientsOverall > 0 ? (totalWeightedScoreOverall / totalCoefficientsOverall) : 0;
        return {
            moyenneGenerale: parseFloat(overallWeightedAverage.toFixed(2)),
            detailsParMatiere: matiereAveragesAndDetails,
            totalPointsOverall: parseFloat(totalWeightedScoreOverall.toFixed(2)),
            totalCoefficientsOverall: totalCoefficientsOverall,
            isPrimaire: isPrimaire
        };
    }, [getMatiereTypeFromLiaison]);

    const calculateAllStudentSubjectAverages = useCallback((classNotes, allMatieres, classMatieresLiaisons, niveau, bulletinType = 'tous') => {
        const niveauLower = (niveau || '').toLowerCase();
        const isPrimaire = niveauLower.includes('élémentaire') || 
                           niveauLower.includes('préscolaire') || 
                           niveauLower.includes('prescolaire');
        
        // Filtrer les liaisons selon le type de bulletin
        let filteredLiaisons = classMatieresLiaisons;
        if (bulletinType !== 'tous') {
            filteredLiaisons = classMatieresLiaisons.filter(liaison => {
                const matiereType = getMatiereTypeFromLiaison(liaison);
                
                if (bulletinType === 'français') {
                    return matiereType === 'français' || matiereType === 'commun';
                } else if (bulletinType === 'arabe') {
                    return matiereType === 'arabe' || matiereType === 'commun';
                }
                return true;
            });
        }
        
        const studentSubjectData = {};
        classNotes.forEach(note => {
            const matiereLiaison = filteredLiaisons.find(
                liaison => liaison.classId === note.classeId && liaison.matiereId === note.matiereId
            );
            if (!matiereLiaison) return;
            
            const eleveId = note.eleveId;
            const noteMax = parseFloat(matiereLiaison.noteSur) || (isPrimaire ? 10 : 20);
            const typeEval = note.typeEvaluation?.toLowerCase() || '';
            
            if (!studentSubjectData[eleveId]) studentSubjectData[eleveId] = {};
            if (!studentSubjectData[eleveId][note.matiereNom]) {
                studentSubjectData[eleveId][note.matiereNom] = { devoirs: [], composition: null };
            }

            if (typeEval.includes('comp') || typeEval.includes('oral')) {
                let v = parseFloat(note.valeurNote);
                if (!isNaN(v)) studentSubjectData[eleveId][note.matiereNom].composition = (noteMax !== 20 ? (v / noteMax) * 20 : v);
            } else if (typeEval.includes('devoir')) {
                [note.valeurNoteDevoir1, note.valeurNoteDevoir2].forEach(val => {
                    let v = parseFloat(val);
                    if (!isNaN(v)) studentSubjectData[eleveId][note.matiereNom].devoirs.push(noteMax !== 20 ? (v / noteMax) * 20 : v);
                });
                if (note.valeurNote && !note.valeurNoteDevoir1 && !note.valeurNoteDevoir2) {
                    let v = parseFloat(note.valeurNote);
                    if (!isNaN(v)) studentSubjectData[eleveId][note.matiereNom].devoirs.push(noteMax !== 20 ? (v / noteMax) * 20 : v);
                }
            }
        });

        const subjectAveragesListWithValidEntries = {};
        Object.keys(studentSubjectData).forEach(eid => {
            Object.keys(studentSubjectData[eid]).forEach(mat => {
                const d = studentSubjectData[eid][mat];
                let moy = 0;
                const moyDev = d.devoirs.length ? d.devoirs.reduce((a, b) => a + b, 0) / d.devoirs.length : 0;
                if (d.composition === null) moy = moyDev;
                else if (d.devoirs.length === 0) moy = d.composition;
                else moy = ((moyDev * 2) + d.composition) / 3;

                if (d.devoirs.length > 0 || d.composition !== null) {
                    if (!subjectAveragesListWithValidEntries[mat]) subjectAveragesListWithValidEntries[mat] = [];
                    subjectAveragesListWithValidEntries[mat].push({ eleveId: eid, moyenneMatiere: moy });
                }
            });
        });

        const subjectRanks = {};
        Object.keys(subjectAveragesListWithValidEntries).forEach(mat => {
            const list = subjectAveragesListWithValidEntries[mat].sort((a, b) => b.moyenneMatiere - a.moyenneMatiere);
            subjectRanks[mat] = {};
            let rank = 1;
            for (let i = 0; i < list.length; i++) {
                if (i > 0 && list[i].moyenneMatiere < list[i - 1].moyenneMatiere) rank = i + 1;
                subjectRanks[mat][list[i].eleveId] = rank;
            }
        });
        return subjectRanks;
    }, [getMatiereTypeFromLiaison]);

    // FONCTION OPTIMISÉE : Récupération des notes des trimestres précédents
    const buildSingleBulletinData = async (eleve, classe, allYearNotes, subjectRanks, studentRank, totalStudents, allPerformances, isPrimaire, logoBase64 = null, bulletinType = 'tous') => {
        const isSecondaire = !isPrimaire;
        
        const liaisonsQuery = query(collection(db, 'schools', schoolId, 'class_matieres'), where('classId', '==', classe.id));
        const liaisonsSnap = await getDocs(liaisonsQuery);
        const classMatieresLiaisons = liaisonsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // DEBUG: Afficher les liaisons
        console.log('=== DEBUG LIAISONS ===');
        console.log('Classe:', classe.name);
        console.log('Total liaisons:', classMatieresLiaisons.length);
        
        classMatieresLiaisons.forEach(liaison => {
            console.log({
                id: liaison.id,
                name: liaison.matiereName,
                type: liaison.matiereType,
                hasType: !!liaison.matiereType,
                derivedType: getMatiereTypeFromLiaison(liaison)
            });
        });
        
        // Calculer pour le trimestre courant avec le type spécifié
        const computed = calculateStudentWeightedAverageAndDetails(eleve.id, classe.id, selectedPeriode, selectedAnneeScolaire, allYearNotes, matieres, classMatieresLiaisons, classe.niveau, bulletinType);
        
        // Calculer les rangs par matière pour ce type spécifique
        const typeSubjectRanks = calculateAllStudentSubjectAverages(allYearNotes, matieres, classMatieresLiaisons, classe.niveau, bulletinType);
        
        // Pour primaire, préparer les notes de tous les trimestres
        let detailsAvecNotesTrimestres = [];
        let notesTrimestresPrecedents = {};

        if (isPrimaire) {
            // Pour primaire, on va chercher les notes de tous les trimestres
            const periodes = ['1er Trimestre', '2ème Trimestre', '3ème Trimestre'];
            
            // Initialiser avec les détails calculés
            detailsAvecNotesTrimestres = computed.detailsParMatiere.map(d => ({
                ...d,
                rankMatiere: typeSubjectRanks[d.matiere]?.[eleve.id] || '-',
                notesTrimestres: { ...d.notesTrimestres } // Copier les notes existantes
            }));

            // Pour chaque trimestre, récupérer les notes depuis les données existantes
            for (const periode of periodes) {
                if (periode === selectedPeriode) {
                    // Pour le trimestre courant, utiliser la moyenne générale calculée
                    notesTrimestresPrecedents[periode] = computed.moyenneGenerale;
                    continue;
                }
                
                // Chercher les notes pour ce trimestre dans les données existantes
                const notesDuTrimestre = allYearNotes.filter(n => 
                    String(n.eleveId) === String(eleve.id) && 
                    String(n.trimestre) === periode
                );
                
                if (notesDuTrimestre.length > 0) {
                    const computedTrimestre = calculateStudentWeightedAverageAndDetails(eleve.id, classe.id, periode, selectedAnneeScolaire, notesDuTrimestre, matieres, classMatieresLiaisons, classe.niveau, bulletinType);
                    notesTrimestresPrecedents[periode] = computedTrimestre.moyenneGenerale;
                    
                    // Ajouter les notes de ce trimestre à chaque matière
                    detailsAvecNotesTrimestres.forEach(detail => {
                        const matiereDetail = computedTrimestre.detailsParMatiere.find(d => d.matiere === detail.matiere);
                        if (matiereDetail && matiereDetail.moyenneMatiere > 0) {
                            detail.notesTrimestres[periode] = matiereDetail.moyenneMatiere;
                        } else {
                            detail.notesTrimestres[periode] = '-';
                        }
                    });
                } else {
                    notesTrimestresPrecedents[periode] = null;
                    // Pour chaque matière, mettre '-' pour ce trimestre
                    detailsAvecNotesTrimestres.forEach(detail => {
                        detail.notesTrimestres[periode] = '-';
                    });
                }
            }
        } else {
            // Pour secondaire
            detailsAvecNotesTrimestres = computed.detailsParMatiere.map(d => ({
                ...d,
                rankMatiere: typeSubjectRanks[d.matiere]?.[eleve.id] || '-'
            }));
            
            if (selectedPeriode !== '1er Trimestre') {
                const periodesPrecedentes = [];
                if (selectedPeriode === '2ème Trimestre') periodesPrecedentes.push('1er Trimestre');
                if (selectedPeriode === '3ème Trimestre') {
                    periodesPrecedentes.push('1er Trimestre');
                    periodesPrecedentes.push('2ème Trimestre');
                }
                
                for (const periodePrec of periodesPrecedentes) {
                    const notesPrec = allYearNotes.filter(n => 
                        String(n.eleveId) === String(eleve.id) && 
                        String(n.trimestre) === periodePrec
                    );
                    
                    if (notesPrec.length > 0) {
                        const computedPrec = calculateStudentWeightedAverageAndDetails(eleve.id, classe.id, periodePrec, selectedAnneeScolaire, notesPrec, matieres, classMatieresLiaisons, classe.niveau, bulletinType);
                        notesTrimestresPrecedents[periodePrec] = computedPrec.moyenneGenerale;
                    }
                }
            }
        }
        
        // Calculer la moyenne de classe pour ce type spécifique
        const typePerformances = allPerformances.filter(p => {
            const pNotes = allYearNotes.filter(n => 
                String(n.eleveId) === String(p.id) && 
                String(n.trimestre) === selectedPeriode
            );
            const pComputed = calculateStudentWeightedAverageAndDetails(p.id, classe.id, selectedPeriode, selectedAnneeScolaire, pNotes, matieres, classMatieresLiaisons, classe.niveau, bulletinType);
            return pComputed.totalCoefficientsOverall > 0;
        }).map(p => {
            const pNotes = allYearNotes.filter(n => 
                String(n.eleveId) === String(p.id) && 
                String(n.trimestre) === selectedPeriode
            );
            const pComputed = calculateStudentWeightedAverageAndDetails(p.id, classe.id, selectedPeriode, selectedAnneeScolaire, pNotes, matieres, classMatieresLiaisons, classe.niveau, bulletinType);
            return { id: p.id, moy: pComputed.moyenneGenerale };
        });
        
        let totalClassMoy = typePerformances.reduce((acc, curr) => acc + curr.moy, 0);
        const moyClasse = typePerformances.length ? parseFloat((totalClassMoy / typePerformances.length).toFixed(2)) : 0;
        
        // Charger les absences
        let absSummary = { totalAbsences: 0, justifiedAbsences: 0, unjustifiedAbsences: 0, totalRetards: 0, justifiedRetards: 0, unjustifiedRetards: 0 };
        
        // Charger les bulletins précédents
        let prevData = [];
        let hist = { m1: null, m2: null, m3: null, s1: null, s2: null, ann: null };

        if (isPrimaire && selectedPeriode === '3ème Trimestre') {
            // Pour primaire au 3ème trimestre, on a besoin des moyennes des trimestres précédents
            hist.m1 = notesTrimestresPrecedents['1er Trimestre'] !== null && notesTrimestresPrecedents['1er Trimestre'] !== undefined ? notesTrimestresPrecedents['1er Trimestre'] : null;
            hist.m2 = notesTrimestresPrecedents['2ème Trimestre'] !== null && notesTrimestresPrecedents['2ème Trimestre'] !== undefined ? notesTrimestresPrecedents['2ème Trimestre'] : null;
            hist.m3 = computed.moyenneGenerale; // Le 3ème trimestre est la moyenne courante
            
            // Calculer la moyenne annuelle seulement au 3ème trimestre
            const trimestresValides = [hist.m1, hist.m2, hist.m3].filter(t => t !== null);
            if (trimestresValides.length >= 2) {
                hist.ann = parseFloat((trimestresValides.reduce((a, b) => a + b, 0) / trimestresValides.length).toFixed(2));
            }
        } else if (!isPrimaire) {
            // Pour secondaire, charger les bulletins précédents
            const prevQ = query(
                collection(db, 'schools', schoolId, 'bulletins'), 
                where('eleveId', '==', eleve.id), 
                where('anneeScolaire', '==', selectedAnneeScolaire), 
                where('periode', 'in', ['Semestre 1', 'Semestre 2'])
            );
            const prevSnap = await getDocs(prevQ);
            prevData = prevSnap.docs.map(d => d.data());
            
            hist.s1 = prevData.find(b => b.periode === 'Semestre 1')?.moyenneGenerale ?? null;
            hist.s2 = selectedPeriode === 'Semestre 2' ? computed.moyenneGenerale : prevData.find(b => b.periode === 'Semestre 2')?.moyenneGenerale ?? null;
            
            if (hist.s1 !== null && hist.s2 !== null) {
                hist.ann = parseFloat(((hist.s1 + hist.s2) / 2).toFixed(2));
            }
        }

        return {
            eleveId: eleve.id,
            eleveNomComplet: eleve.name || `${eleve.prenom} ${eleve.nom}`,
            classeId: classe.id,
            classeNom: classe.name,
            periode: selectedPeriode,
            anneeScolaire: selectedAnneeScolaire,
            dateGeneration: new Date().toISOString(),
            moyenneGenerale: computed.moyenneGenerale,
            totalPointsOverall: computed.totalPointsOverall,
            totalCoefficientsOverall: computed.totalCoefficientsOverall,
            detailsParMatiere: detailsAvecNotesTrimestres,
            rank: studentRank,
            moyenneClasse: moyClasse,
            baseNotation: parseInt(classe.baseNotation || (isPrimaire ? 10 : 20)),
            totalStudentsInClass: totalStudents,
            appreciations: generateAppreciation(computed.moyenneGenerale),
            schoolInfo: { ...schoolSettings, logoBase64: logoBase64 },
            eleveDetails: { ...eleve, dateDeNaissance: eleve.dateOfBirth, placeOfBirth: eleve.placeOfBirth, address: eleve.address },
            ien: schoolSettings?.ien || 'N/A',
            niveau: classe?.niveau || 'N/A',
            effectifClasse: totalStudents,
            absencesRetardsSummary: absSummary,
            moyenne1erTrimestre: hist.m1,
            moyenne2eTrimestre: hist.m2,
            moyenne3eTrimestre: hist.m3,
            moyenneSemestre1: hist.s1,
            moyenneSemestre2: hist.s2,
            moyenneAnnuelle: hist.ann,
            isPrimaire,
            isSecondaire,
            notesTrimestresPrecedents: notesTrimestresPrecedents,
            bulletinType: bulletinType,
            isFrancoArabe: schoolSettings?.isFrancoArabe || false
        };
    };

    const calculateBulletin = async () => {
        if (!selectedEleveId || !selectedPeriode || !selectedAnneeScolaire || !schoolId) {
            setErrorMessage('Veuillez remplir tous les champs.');
            return;
        }
        
        setGeneratingBulletin(true);
        setErrorMessage('');
        setGeneratedBulletin(null);
        setFrenchBulletin(null);
        setArabicBulletin(null);
        setStatistics(null);
        
        try {
            const eleve = eleves.find(e => String(e.id) === String(selectedEleveId));
            if (!eleve) throw new Error("Élève introuvable.");

            const enrollmentQ = query(collection(db, 'schools', schoolId, 'enrollments'), where('studentId', '==', selectedEleveId), where('schoolYear', '==', selectedAnneeScolaire), limit(1));
            const [enrollSnap] = await Promise.all([getDocs(enrollmentQ)]);

            if (enrollSnap.empty) throw new Error("Élève non inscrit cette année.");
            const classId = enrollSnap.docs[0].data().classId;
            const classe = classes.find(c => c.id === classId);
            if (!classe) throw new Error("Classe introuvable.");

            // Déterminer si c'est primaire
            const niveau = (classe?.niveau || '').toLowerCase();
            const isPrimaire = niveau.includes('élémentaire') || 
                               niveau.includes('préscolaire') || 
                               niveau.includes('prescolaire') ||
                               niveau.includes('primaire') ||
                               niveau.includes('maternelle');

            // Vérifier si c'est une école franco-arabe
            const isFrancoArabe = schoolSettings?.isFrancoArabe || false;

            // Charger TOUTES les notes de l'année pour cette classe (1 appel Firestore)
            const allYearNotesSnap = await getDocs(
                query(collection(db, 'schools', schoolId, 'notes'), 
                where('classeId', '==', classId), 
                where('anneeScolaire', '==', selectedAnneeScolaire))
            );
            const allYearNotes = allYearNotesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Charger les liaisons matières (1 appel Firestore)
            const liaisonsQuery = query(collection(db, 'schools', schoolId, 'class_matieres'), where('classId', '==', classId));
            const liaisonsSnap = await getDocs(liaisonsQuery);
            const classMatieresLiaisons = liaisonsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            const classStudentsSnap = await getDocs(query(collection(db, 'schools', schoolId, 'enrollments'), where('classId', '==', classId), where('schoolYear', '==', selectedAnneeScolaire)));
            const studentIdsInClass = classStudentsSnap.docs.map(d => d.data().studentId);
            const allClassStudents = eleves.filter(e => studentIdsInClass.includes(e.id));
            
            const logoBase64 = await urlToBase64(schoolSettings?.logoUrl);
            
            if (isFrancoArabe) {
                // École franco-arabe: générer les trois types de bulletins
                
                // 1. Bulletin complet (toutes matières)
                const subjectRanksAll = calculateAllStudentSubjectAverages(allYearNotes, matieres, classMatieresLiaisons, classe.niveau, 'tous');
                const performancesAll = allClassStudents.map(s => {
                    const sNotes = allYearNotes.filter(n => 
                        String(n.eleveId) === String(s.id) && 
                        String(n.trimestre) === selectedPeriode
                    );
                    const res = calculateStudentWeightedAverageAndDetails(s.id, classId, selectedPeriode, selectedAnneeScolaire, sNotes, matieres, classMatieresLiaisons, classe.niveau, 'tous');
                    return { id: s.id, moy: res.moyenneGenerale, valid: res.totalCoefficientsOverall > 0 };
                }).filter(p => p.valid).sort((a, b) => b.moy - a.moy);
                
                let rankMapAll = {};
                let currentRankAll = 1;
                performancesAll.forEach((p, i) => {
                    if (i > 0 && p.moy < performancesAll[i - 1].moy) currentRankAll = i + 1;
                    rankMapAll[p.id] = currentRankAll;
                });
                
                const bulletinDataAll = await buildSingleBulletinData(
                    eleve, classe, allYearNotes, subjectRanksAll, rankMapAll[eleve.id] || '-', allClassStudents.length, performancesAll, isPrimaire, logoBase64, 'tous'
                );
                
                // 2. Bulletin français seulement
                const subjectRanksFrench = calculateAllStudentSubjectAverages(allYearNotes, matieres, classMatieresLiaisons, classe.niveau, 'français');
                const performancesFrench = allClassStudents.map(s => {
                    const sNotes = allYearNotes.filter(n => 
                        String(n.eleveId) === String(s.id) && 
                        String(n.trimestre) === selectedPeriode
                    );
                    const res = calculateStudentWeightedAverageAndDetails(s.id, classId, selectedPeriode, selectedAnneeScolaire, sNotes, matieres, classMatieresLiaisons, classe.niveau, 'français');
                    return { id: s.id, moy: res.moyenneGenerale, valid: res.totalCoefficientsOverall > 0 };
                }).filter(p => p.valid).sort((a, b) => b.moy - a.moy);
                
                let rankMapFrench = {};
                let currentRankFrench = 1;
                performancesFrench.forEach((p, i) => {
                    if (i > 0 && p.moy < performancesFrench[i - 1].moy) currentRankFrench = i + 1;
                    rankMapFrench[p.id] = currentRankFrench;
                });
                
                const frenchBulletinData = await buildSingleBulletinData(
                    eleve, classe, allYearNotes, subjectRanksFrench, rankMapFrench[eleve.id] || '-', allClassStudents.length, performancesFrench, isPrimaire, logoBase64, 'français'
                );
                
                // 3. Bulletin arabe seulement
                const subjectRanksArabic = calculateAllStudentSubjectAverages(allYearNotes, matieres, classMatieresLiaisons, classe.niveau, 'arabe');
                const performancesArabic = allClassStudents.map(s => {
                    const sNotes = allYearNotes.filter(n => 
                        String(n.eleveId) === String(s.id) && 
                        String(n.trimestre) === selectedPeriode
                    );
                    const res = calculateStudentWeightedAverageAndDetails(s.id, classId, selectedPeriode, selectedAnneeScolaire, sNotes, matieres, classMatieresLiaisons, classe.niveau, 'arabe');
                    return { id: s.id, moy: res.moyenneGenerale, valid: res.totalCoefficientsOverall > 0 };
                }).filter(p => p.valid).sort((a, b) => b.moy - a.moy);
                
                let rankMapArabic = {};
                let currentRankArabic = 1;
                performancesArabic.forEach((p, i) => {
                    if (i > 0 && p.moy < performancesArabic[i - 1].moy) currentRankArabic = i + 1;
                    rankMapArabic[p.id] = currentRankArabic;
                });
                
                const arabicBulletinData = await buildSingleBulletinData(
                    eleve, classe, allYearNotes, subjectRanksArabic, rankMapArabic[eleve.id] || '-', allClassStudents.length, performancesArabic, isPrimaire, logoBase64, 'arabe'
                );
                
                setGeneratedBulletin(bulletinDataAll);
                setFrenchBulletin(frenchBulletinData);
                setArabicBulletin(arabicBulletinData);
                setAppreciationText(bulletinDataAll.appreciations);
                setFrenchAppreciationText(frenchBulletinData.appreciations);
                setArabicAppreciationText(arabicBulletinData.appreciations);
                
                // Statistiques pour le type sélectionné
                const selectedData = selectedBulletinType === 'français' ? frenchBulletinData : 
                                   selectedBulletinType === 'arabe' ? arabicBulletinData : 
                                   bulletinDataAll;
                setStatistics(calculateStatistics([selectedData]));
                
            } else {
                // École normale: générer seulement le bulletin demandé
                const subjectRanks = calculateAllStudentSubjectAverages(allYearNotes, matieres, classMatieresLiaisons, classe.niveau, selectedBulletinType);
                const performances = allClassStudents.map(s => {
                    const sNotes = allYearNotes.filter(n => 
                        String(n.eleveId) === String(s.id) && 
                        String(n.trimestre) === selectedPeriode
                    );
                    const res = calculateStudentWeightedAverageAndDetails(s.id, classId, selectedPeriode, selectedAnneeScolaire, sNotes, matieres, classMatieresLiaisons, classe.niveau, selectedBulletinType);
                    return { id: s.id, moy: res.moyenneGenerale, valid: res.totalCoefficientsOverall > 0 };
                }).filter(p => p.valid).sort((a, b) => b.moy - a.moy);
                
                let rankMap = {};
                let currentRank = 1;
                performances.forEach((p, i) => {
                    if (i > 0 && p.moy < performances[i - 1].moy) currentRank = i + 1;
                    rankMap[p.id] = currentRank;
                });
                
                const bulletinData = await buildSingleBulletinData(
                    eleve, classe, allYearNotes, subjectRanks, rankMap[eleve.id] || '-', allClassStudents.length, performances, isPrimaire, logoBase64, selectedBulletinType
                );
                
                setGeneratedBulletin(bulletinData);
                setAppreciationText(bulletinData.appreciations);
                setStatistics(calculateStatistics([bulletinData]));
            }
            
            setSuccessMessage("Bulletin généré avec succès.");
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message);
        } finally {
            setGeneratingBulletin(false);
        }
    };

    const handleBulkGeneration = async () => {
        if (!selectedClasseIdForBulk || !selectedPeriode || !selectedAnneeScolaire) {
            setErrorMessage("Veuillez sélectionner une classe, une période et une année.");
            return;
        }

        setGeneratingBulletin(true);
        setErrorMessage('');
        setSuccessMessage('');
        setBulkBulletins([]);
        setStatistics(null);
        
        try {
            const classe = classes.find(c => c.id === selectedClasseIdForBulk);
            if (!classe) throw new Error("Classe introuvable.");

            // Déterminer si c'est primaire
            const niveau = (classe?.niveau || '').toLowerCase();
            const isPrimaire = niveau.includes('élémentaire') || 
                               niveau.includes('préscolaire') || 
                               niveau.includes('prescolaire') ||
                               niveau.includes('primaire') ||
                               niveau.includes('maternelle');

            const enrollSnap = await getDocs(query(collection(db, 'schools', schoolId, 'enrollments'), where('classId', '==', selectedClasseIdForBulk), where('schoolYear', '==', selectedAnneeScolaire)));
            if (enrollSnap.empty) throw new Error("Aucun élève inscrit.");
            
            const studentIds = enrollSnap.docs.map(d => d.data().studentId);
            const classStudents = eleves.filter(e => studentIds.includes(e.id));
            
            // Charger TOUTES les notes de l'année pour cette classe (1 seul appel Firestore)
            const allYearNotesSnap = await getDocs(
                query(collection(db, 'schools', schoolId, 'notes'), 
                where('classeId', '==', selectedClasseIdForBulk), 
                where('anneeScolaire', '==', selectedAnneeScolaire))
            );
            const allYearNotes = allYearNotesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Charger les liaisons matières (1 appel Firestore)
            const liaisonsQuery = query(collection(db, 'schools', schoolId, 'class_matieres'), where('classId', '==', selectedClasseIdForBulk));
            const liaisonsSnap = await getDocs(liaisonsQuery);
            const classMatieresLiaisons = liaisonsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const subjectRanks = calculateAllStudentSubjectAverages(allYearNotes, matieres, classMatieresLiaisons, classe.niveau, selectedBulletinType);
            
            // Calculer les performances pour le trimestre courant
            const performances = classStudents.map(s => {
                const sNotes = allYearNotes.filter(n => 
                    String(n.eleveId) === String(s.id) && 
                    String(n.trimestre) === selectedPeriode
                );
                const res = calculateStudentWeightedAverageAndDetails(s.id, selectedClasseIdForBulk, selectedPeriode, selectedAnneeScolaire, sNotes, matieres, classMatieresLiaisons, classe.niveau, selectedBulletinType);
                return { id: s.id, moy: res.moyenneGenerale, valid: res.totalCoefficientsOverall > 0 };
            }).filter(p => p.valid).sort((a, b) => b.moy - a.moy);
            
            let rankMap = {};
            let currentRank = 1;
            performances.forEach((p, i) => {
                if (i > 0 && p.moy < performances[i - 1].moy) currentRank = i + 1;
                rankMap[p.id] = currentRank;
            });

            const logoBase64 = await urlToBase64(schoolSettings?.logoUrl);
            
            // Générer tous les bulletins en parallèle
            const bulletinPromises = classStudents.map(student => {
                return buildSingleBulletinData(
                    student, classe, allYearNotes, subjectRanks, rankMap[student.id] || '-', 
                    classStudents.length, performances, isPrimaire, logoBase64, selectedBulletinType
                );
            });
            
            const results = await Promise.all(bulletinPromises);
            
            results.sort((a, b) => {
                const rankA = typeof a.rank === 'number' ? a.rank : 9999;
                const rankB = typeof b.rank === 'number' ? b.rank : 9999;
                return rankA - rankB;
            });
            
            setBulkBulletins(results);
            setStatistics(calculateStatistics(results));
            setSuccessMessage(`${results.length} bulletins générés pour la classe ${classe.name}.`);
        } catch (err) {
            console.error("Bulk Error:", err);
            setErrorMessage(`Erreur masse: ${err.message}`);
        } finally {
            setGeneratingBulletin(false);
        }
    };

    const calculateStatistics = (bulletins) => {
    if (!bulletins || bulletins.length === 0) return null;
    
    // Pour primaire au 3ème trimestre, utiliser la moyenne annuelle si disponible
    const moyennes = bulletins.map(b => {
        if (b.isPrimaire && b.periode === '3ème Trimestre' && b.moyenneAnnuelle !== null && !isNaN(b.moyenneAnnuelle)) {
            return b.moyenneAnnuelle;
        }
        return b.moyenneGenerale;
    });
    
    const max = Math.max(...moyennes);
    const min = Math.min(...moyennes);
    const avg = moyennes.reduce((a, b) => a + b, 0) / moyennes.length;
    
    // Fonction pour déterminer la mention basée sur la base de notation
    const getMention = (moyenne, baseNotation) => {
        // Ajuster si la moyenne est sur 10
        if (baseNotation === 10) {
            if (moyenne >= 8) return "excellent";
            else if (moyenne >= 7) return "bien";
            else if (moyenne >= 6) return "assezBien";
            else if (moyenne >= 5) return "passable";
            else return "insuffisant";
        } else {
            // Base 20
            if (moyenne >= 16) return "excellent";
            else if (moyenne >= 14) return "bien";
            else if (moyenne >= 12) return "assezBien";
            else if (moyenne >= 10) return "passable";
            else return "insuffisant";
        }
    };

    const distribution = {
        excellent: 0,
        bien: 0,
        assezBien: 0,
        passable: 0,
        insuffisant: 0
    };

    // Compter les mentions selon la base de notation de chaque élève
    bulletins.forEach(b => {
        const moyenne = b.isPrimaire && b.periode === '3ème Trimestre' && b.moyenneAnnuelle !== null && !isNaN(b.moyenneAnnuelle) 
            ? b.moyenneAnnuelle 
            : b.moyenneGenerale;
        
        const mention = getMention(moyenne, b.baseNotation || (b.isPrimaire ? 10 : 20));
        distribution[mention]++;
    });

    // Statistiques supplémentaires pour primaire
    const primaireStats = bulletins[0]?.isPrimaire ? {
        totalPrimaire: bulletins.filter(b => b.isPrimaire).length,
        avecMoyenneAnnuelle: bulletins.filter(b => 
            b.isPrimaire && b.periode === '3ème Trimestre' && 
            b.moyenneAnnuelle !== null && !isNaN(b.moyenneAnnuelle)
        ).length,
        moyennesTrimestres: {
            trim1: bulletins.filter(b => b.moyenne1erTrimestre !== null && !isNaN(b.moyenne1erTrimestre)).length,
            trim2: bulletins.filter(b => b.moyenne2eTrimestre !== null && !isNaN(b.moyenne2eTrimestre)).length,
            trim3: bulletins.filter(b => b.moyenne3eTrimestre !== null && !isNaN(b.moyenne3eTrimestre)).length
        }
    } : null;

    // Statistiques pour franco-arabe
    const francoArabeStats = schoolSettings?.isFrancoArabe ? {
        typeBulletin: bulletins[0]?.bulletinType || 'tous',
        matieresFrancaises: bulletins[0]?.detailsParMatiere?.filter(m => m.matiereType === 'français').length || 0,
        matieresArabes: bulletins[0]?.detailsParMatiere?.filter(m => m.matiereType === 'arabe').length || 0,
        matieresCommunes: bulletins[0]?.detailsParMatiere?.filter(m => m.matiereType === 'commun').length || 0
    } : null;

    return { 
        max: parseFloat(max.toFixed(2)), 
        min: parseFloat(min.toFixed(2)), 
        avg: parseFloat(avg.toFixed(2)), 
        distribution, 
        total: bulletins.length,
        primaireStats,
        francoArabeStats,
        isPrimaire: bulletins[0]?.isPrimaire || false,
        periode: bulletins[0]?.periode || '',
        bulletinType: bulletins[0]?.bulletinType || 'tous'
    };
};

    const handleSaveBulletin = async () => {
        if (!generatedBulletin) return;
        setGeneratingBulletin(true);
        try {
            const colRef = collection(db, 'schools', schoolId, 'bulletins');
            const q = query(colRef, where('eleveId', '==', generatedBulletin.eleveId), where('classeId', '==', generatedBulletin.classeId), where('periode', '==', generatedBulletin.periode), where('anneeScolaire', '==', generatedBulletin.anneeScolaire), limit(1));
            const snap = await getDocs(q);
            
            // Préparer les données
            let dataToSave = { ...generatedBulletin };
            
            // Gérer l'appréciation selon le type de bulletin
            if (schoolSettings?.isFrancoArabe) {
                if (selectedBulletinType === 'français' && frenchBulletin) {
                    dataToSave = { ...frenchBulletin, appreciations: frenchAppreciationText };
                } else if (selectedBulletinType === 'arabe' && arabicBulletin) {
                    dataToSave = { ...arabicBulletin, appreciations: arabicAppreciationText };
                } else {
                    dataToSave.appreciations = appreciationText;
                }
            } else {
                dataToSave.appreciations = appreciationText;
            }
            
            dataToSave = {
                ...dataToSave,
                createdAt: new Date().toISOString(),
                updatedAt: serverTimestamp(),
                bulletinType: selectedBulletinType
            };
            
            if (!snap.empty) {
                await updateDoc(doc(colRef, snap.docs[0].id), dataToSave);
            } else {
                await addDoc(colRef, dataToSave);
            }
            setSuccessMessage(`Bulletin sauvegardé.`);
        } catch (e) {
            setErrorMessage(e.message);
        }
        setGeneratingBulletin(false);
    };

    const handleSaveBulkBulletins = async () => {
        if (bulkBulletins.length === 0) return;
        setGeneratingBulletin(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const colRef = collection(db, 'schools', schoolId, 'bulletins');
            const batch = writeBatch(db);
            
            // Récupérer les bulletins existants en une seule requête
            const existingBulletinsQuery = query(
                colRef, 
                where('classeId', '==', bulkBulletins[0].classeId), 
                where('periode', '==', bulkBulletins[0].periode), 
                where('anneeScolaire', '==', bulkBulletins[0].anneeScolaire)
            );
            const existingSnap = await getDocs(existingBulletinsQuery);
            const existingMap = new Map();
            existingSnap.docs.forEach(doc => { existingMap.set(doc.data().eleveId, doc.id); });

            let savedCount = 0;
            let errorCount = 0;
            for (const bulletin of bulkBulletins) {
                try {
                    const data = { 
                        ...bulletin, 
                        appreciations: bulletin.appreciations, 
                        createdAt: new Date().toISOString(), 
                        updatedAt: serverTimestamp(),
                        bulletinType: selectedBulletinType
                    };
                    if (existingMap.has(bulletin.eleveId)) {
                        const docId = existingMap.get(bulletin.eleveId);
                        batch.update(doc(colRef, docId), data);
                    } else {
                        const newDocRef = doc(colRef);
                        batch.set(newDocRef, data);
                    }
                    savedCount++;
                } catch (e) {
                    console.error(`Error preparing bulletin for ${bulletin.eleveNomComplet}:`, e);
                    errorCount++;
                }
            }
            await batch.commit();
            setSuccessMessage(`Opération terminée. ${savedCount} bulletins sauvegardés/mis à jour. ${errorCount > 0 ? `(${errorCount} erreurs de prépa)` : ''}`);
        } catch (e) {
            setErrorMessage(e.message);
        } finally {
            setGeneratingBulletin(false);
        }
    };

    const handleDownloadPDF = async (mode) => {
        setGeneratingBulletin(true);
        setErrorMessage('');
        setPdfProgress({ current: 0, total: 0, message: 'Initialisation...' });
        
        const targetId = mode === 'individual' ? 'bulletin-to-print' : 'bulk-bulletins-container';
        const input = document.getElementById(targetId);

        if (!input) {
            setErrorMessage("Rien à télécharger.");
            setGeneratingBulletin(false);
            setPdfProgress({ current: 0, total: 0, message: '' });
            return;
        }

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const A4_WIDTH = pdf.internal.pageSize.getWidth();
            const MARGIN = 10;
            const TARGET_WIDTH = A4_WIDTH - (2 * MARGIN);
            const elementsToProcess = mode === 'class' ? Array.from(input.querySelectorAll('.bulletin-wrapper')) : [input];
            const total = elementsToProcess.length;
            setPdfProgress({ current: 0, total, message: `Préparation de ${total} bulletin(s)...` });
            
            const captureContainer = document.createElement('div');
            captureContainer.style.position = 'fixed';
            captureContainer.style.left = '-9999px';
            captureContainer.style.top = '-9999px';
            captureContainer.style.width = '100%';
            captureContainer.style.zIndex = '-9999';
            captureContainer.style.backgroundColor = '#ffffff';
            document.body.appendChild(captureContainer);

            for (let i = 0; i < elementsToProcess.length; i++) {
                const originalElement = elementsToProcess[i];
                const clone = originalElement.cloneNode(true);
                clone.style.transform = '';
                clone.style.transformOrigin = '';
                clone.style.width = '';
                clone.style.margin = '';
                clone.style.padding = '20px';
                clone.style.backgroundColor = '#ffffff';
                clone.style.boxSizing = 'border-box';
                captureContainer.appendChild(clone);
                
                const eleveNom = mode === 'class' ? bulkBulletins[i]?.eleveNomComplet : generatedBulletin?.eleveNomComplet;
                setPdfProgress({ current: i + 1, total, message: `Capture du bulletin de ${eleveNom || 'l\'élève'}...` });
                
                const html2canvasConfig = { scale: 2, useCORS: true, logging: false, allowTaint: true, backgroundColor: '#ffffff', width: clone.scrollWidth, height: clone.scrollHeight, onclone: (clonedDoc) => {
                    const clonedClone = clonedDoc.querySelector(`#${clone.id}`);
                    if (clonedClone) { clonedClone.style.boxShadow = 'none'; clonedClone.style.border = '1px solid #000'; }
                }};
                
                const canvas = await html2canvas(clone, html2canvasConfig);
                clone.remove();
                const imgData = canvas.toDataURL('image/jpeg', 1.0);
                const imgHeight = (canvas.height * TARGET_WIDTH) / canvas.width;
                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', MARGIN, MARGIN, TARGET_WIDTH, imgHeight);
            }
            captureContainer.remove();
            
            // Nom du fichier selon le type de bulletin
            let fileName = '';
            if (mode === 'individual') {
                const typeSuffix = selectedBulletinType === 'français' ? '_FRANCAIS' : 
                                 selectedBulletinType === 'arabe' ? '_ARABE' : '';
                fileName = `Bulletin${typeSuffix}_${generatedBulletin?.eleveNomComplet?.replace(/\s/g, '_')}_${generatedBulletin?.periode?.replace(/\s/g, '')}.pdf`;
            } else {
                const typeSuffix = selectedBulletinType === 'français' ? '_FRANCAIS' : 
                                 selectedBulletinType === 'arabe' ? '_ARABE' : '';
                fileName = `Bulletins${typeSuffix}_Classe_${classes.find(c => c.id === selectedClasseIdForBulk)?.name?.replace(/\s/g, '_')}_${selectedPeriode?.replace(/\s/g, '')}.pdf`;
            }
            
            pdf.save(fileName);
            setSuccessMessage("Le fichier PDF a été téléchargé avec succès.");
        } catch (e) {
            console.error("PDF Generation Error:", e);
            setErrorMessage(`Erreur lors du téléchargement du PDF: ${e.message}.`);
        } finally {
            setGeneratingBulletin(false);
            setPdfProgress({ current: 0, total: 0, message: '' });
        }
    };

    const handleExportExcel = () => {
        const dataToExport = viewMode === 'individual' ? [generatedBulletin] : bulkBulletins;
        if (dataToExport.length === 0) {
            setErrorMessage("Aucune donnée à exporter.");
            return;
        }
        try {
            const excelData = dataToExport.map(bulletin => ({
                'Élève': bulletin.eleveNomComplet,
                'Classe': bulletin.classeNom,
                'Période': bulletin.periode,
                'Année Scolaire': bulletin.anneeScolaire,
                'Moyenne Générale': bulletin.moyenneGenerale,
                'Rang': `${bulletin.rank}/${bulletin.totalStudentsInClass}`,
                'Moyenne Classe': bulletin.moyenneClasse,
                'Total Coefficients': bulletin.totalCoefficientsOverall,
                'Total Points': bulletin.totalPointsOverall,
                'Appréciation': bulletin.appreciations,
                'Date Génération': new Date(bulletin.dateGeneration).toLocaleDateString('fr-FR'),
                'Niveau': bulletin.niveau,
                'Type': bulletin.isPrimaire ? 'Primaire' : 'Secondaire',
                'Type Bulletin': bulletin.bulletinType === 'français' ? 'Français' : bulletin.bulletinType === 'arabe' ? 'Arabe' : 'Complet',
                'Moyenne Annuelle': bulletin.isPrimaire && bulletin.periode === '3ème Trimestre' ? bulletin.moyenneAnnuelle || 'N/A' : 'N/A',
                'École Franco-Arabe': schoolSettings?.isFrancoArabe ? 'Oui' : 'Non'
            }));
            const ws = XLSX.utils.json_to_sheet(excelData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Bulletins");
            const fileName = `bulletins_${selectedBulletinType}_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(wb, fileName);
            setSuccessMessage("Fichier Excel exporté avec succès !");
        } catch (error) {
            console.error("Excel Export Error:", error);
            setErrorMessage("Erreur lors de l'export Excel.");
        }
    };

    const handlePrint = () => {
        if (!generatedBulletin && bulkBulletins.length === 0) {
            setErrorMessage("Aucun bulletin à imprimer.");
            return;
        }
        const printContent = viewMode === 'individual' ? document.getElementById('bulletin-to-print') : document.getElementById('bulk-bulletins-container');
        if (!printContent) {
            setErrorMessage("Contenu d'impression non trouvé.");
            return;
        }
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Bulletin Scolaire</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20mm; }
                        .bulletin-wrapper { max-width: 100%; margin: 0 auto; }
                        @media print { body { margin: 0; padding: 0; } .page-break { page-break-after: always; } }
                    </style>
                </head>
                <body>${printContent.innerHTML}</body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 500);
    };

    return (
        <div className="p-6">
            <h2 className="text-5xl font-bold mb-6 text-gray-800 flex items-center">
                <FileText className="mr-3 text-blue-600" size={28} /> Gestion des Bulletins
            </h2>

            {errorMessage && <FlashMessage type="error" text={errorMessage} onClose={() => setErrorMessage('')} />}
            {successMessage && <FlashMessage type="success" text={successMessage} onClose={() => setSuccessMessage('')} />}
            
            {loading && (
                <div className="flex items-center justify-center p-8">
                    <Loader2 className="animate-spin mr-2 text-blue-600" />
                    <span>Chargement des données...</span>
                </div>
            )}
            
            {generatingBulletin && pdfProgress.total > 0 ? (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center">
                        <Loader2 className="animate-spin mr-2 text-blue-600" />
                        <div className="flex-1">
                            <p className="font-medium text-blue-800">{pdfProgress.message}</p>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(pdfProgress.current / pdfProgress.total) * 100}%` }}></div>
                            </div>
                            <p className="text-sm text-blue-700 mt-1 text-right">{pdfProgress.current} / {pdfProgress.total} ({((pdfProgress.current / pdfProgress.total) * 100).toFixed(0)}%)</p>
                        </div>
                    </div>
                </div>
            ) : generatingBulletin && (
                <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200 flex items-center">
                    <Loader2 className="animate-spin mr-2 text-yellow-600" />
                    <span className="text-yellow-700">Génération des données en cours...</span>
                </div>
            )}

            <div className="mb-8 flex space-x-4 print-hidden">
                <button
                    onClick={() => { setViewMode('individual'); setStatistics(null); }}
                    className={`px-6 py-3 rounded-lg font-semibold transition flex items-center ${viewMode === 'individual' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                    <User size={22} className="mr-2" /> Individuel
                </button>
                <button
                    onClick={() => { setViewMode('class'); setStatistics(null); }}
                    className={`px-6 py-3 rounded-lg font-semibold transition flex items-center ${viewMode === 'class' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                    <Users size={22} className="mr-2" /> Par Classe (Masse)
                </button>
            </div>

            <div className="mb-8 p-6 border border-gray-200 rounded-lg bg-gray-50 print-hidden shadow-sm">
                <h3 className="text-lg font-semibold mb-4 text-gray-700">Paramètres de génération</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    {viewMode === 'individual' ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sélectionner un Élève *</label>
                            <select value={selectedEleveId} onChange={(e) => setSelectedEleveId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" disabled={generatingBulletin}>
                                <option value="">-- Choisir un élève --</option>
                                {eleves.map(e => (
                                    <option key={e.id} value={e.id}>{e.name || `${e.prenom} ${e.nom}`}</option>
                                ))}
                            </select>
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sélectionner une Classe *</label>
                            <select value={selectedClasseIdForBulk} onChange={(e) => setSelectedClasseIdForBulk(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" disabled={generatingBulletin}>
                                <option value="">-- Choisir une classe --</option>
                                {classes.map(c => (
                                    <option key={c.id} value={c.id}>{c.name} {c.niveau ? `(${c.niveau})` : ''}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Période *</label>
                        <select value={selectedPeriode} onChange={(e) => setSelectedPeriode(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" disabled={generatingBulletin}>
                            <option value="">-- Choisir --</option>
                            {periodeOptions.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Année Scolaire *</label>
                        <select value={selectedAnneeScolaire} onChange={(e) => setSelectedAnneeScolaire(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" disabled={generatingBulletin}>
                            <option value="">-- Choisir --</option>
                            {anneeScolaireOptions.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Type de Bulletin *</label>
                        <select 
                            value={selectedBulletinType} 
                            onChange={(e) => setSelectedBulletinType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={generatingBulletin}
                        >
                            {bulletinTypeOptions.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        {schoolSettings?.isFrancoArabe && (
                            <p className="text-xs text-green-600 mt-1">
                                École Franco-Arabe - Sélectionnez le type de bulletin à générer
                            </p>
                        )}
                    </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                    {viewMode === 'individual' ? (
                        <button onClick={calculateBulletin} disabled={generatingBulletin || !selectedEleveId || !selectedPeriode || !selectedAnneeScolaire} className="flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                            {generatingBulletin ? <span className="flex items-center"><Loader2 className="animate-spin mr-2" /> Génération...</span> : <span className="flex items-center"><FileText className="mr-2" /> Générer Bulletin</span>}
                        </button>
                    ) : (
                        <button onClick={handleBulkGeneration} disabled={generatingBulletin || !selectedClasseIdForBulk || !selectedPeriode || !selectedAnneeScolaire} className="flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                            {generatingBulletin ? <span className="flex items-center"><Loader2 className="animate-spin mr-2" /> Génération...</span> : <span className="flex items-center"><Users className="mr-2" /> Générer Bulletins (Masse)</span>}
                        </button>
                    )}
                    
                    {viewMode === 'individual' && generatedBulletin && (
                        <>
                            <button onClick={handleSaveBulletin} disabled={generatingBulletin} className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                                <Save className="mr-2" /> Sauvegarder
                            </button>
                            <button onClick={() => handleDownloadPDF('individual')} disabled={generatingBulletin} className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed">
                                <Download className="mr-2" /> Télécharger PDF
                            </button>
                            <button onClick={handlePrint} disabled={generatingBulletin} className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed">
                                <Printer className="mr-2" /> Imprimer
                            </button>
                        </>
                    )}
                    
                    {viewMode === 'class' && bulkBulletins.length > 0 && (
                        <>
                            <button onClick={handleSaveBulkBulletins} disabled={generatingBulletin} className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                                <Save className="mr-2" /> Sauvegarder Tous ({bulkBulletins.length})
                            </button>
                            <button onClick={() => handleDownloadPDF('class')} disabled={generatingBulletin} className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed">
                                <Download className="mr-2" /> Télécharger PDF
                            </button>
                            <button onClick={handleExportExcel} disabled={generatingBulletin} className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-700 hover:bg-green-800 transition disabled:bg-gray-400 disabled:cursor-not-allowed">
                                <FileSpreadsheet className="mr-2" /> Exporter Excel
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Section Statistiques */}
            {statistics && (
                <div className="mb-6 p-6 bg-white rounded-lg shadow border border-gray-200">
                    <div className="flex items-center mb-4">
                        <BarChart className="mr-2 text-blue-600" />
                        <h3 className="text-xl font-semibold text-gray-800">📊 Statistiques</h3>
                        {schoolSettings?.isFrancoArabe && (
                            <span className="ml-4 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                                Franco-Arabe - {statistics.bulletinType === 'français' ? 'Section Française' : 
                                               statistics.bulletinType === 'arabe' ? 'Section Arabe' : 'Section Complète'}
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                                Moyenne {statistics.isPrimaire && statistics.periode === '3ème Trimestre' ? 'Annuelle Max' : 'Max'}
                            </p>
                            <p className="text-2xl font-bold text-blue-700">{statistics.max}</p>
                        </div>
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                                Moyenne {statistics.isPrimaire && statistics.periode === '3ème Trimestre' ? 'Annuelle Min' : 'Min'}
                            </p>
                            <p className="text-2xl font-bold text-blue-700">{statistics.min}</p>
                        </div>
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                                Moyenne {statistics.isPrimaire && statistics.periode === '3ème Trimestre' ? 'Annuelle Classe' : 'Classe'}
                            </p>
                            <p className="text-2xl font-bold text-blue-700">{statistics.avg}</p>
                        </div>
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm text-gray-600">Élèves</p>
                            <p className="text-2xl font-bold text-blue-700">{statistics.total}</p>
                        </div>
                    </div>
                    
                    {/* Statistiques Franco-Arabe */}
                    {statistics.francoArabeStats && (
                        <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                            <h4 className="font-semibold text-green-800 mb-3">📋 Détails Franco-Arabe</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="text-center p-3 bg-white rounded-lg border border-blue-200">
                                    <p className="text-sm text-blue-700 font-medium">Matières Françaises</p>
                                    <p className="text-xl font-bold text-blue-800">{statistics.francoArabeStats.matieresFrancaises}</p>
                                </div>
                                <div className="text-center p-3 bg-white rounded-lg border border-green-200">
                                    <p className="text-sm text-green-700 font-medium">Matières Arabes</p>
                                    <p className="text-xl font-bold text-green-800">{statistics.francoArabeStats.matieresArabes}</p>
                                </div>
                                <div className="text-center p-3 bg-white rounded-lg border border-purple-200">
                                    <p className="text-sm text-purple-700 font-medium">Matières Communes</p>
                                    <p className="text-xl font-bold text-purple-800">{statistics.francoArabeStats.matieresCommunes}</p>
                                </div>
                                <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                                    <p className="text-sm text-gray-700 font-medium">Type Bulletin</p>
                                    <p className="text-xl font-bold text-gray-800">
                                        {statistics.francoArabeStats.typeBulletin === 'français' ? 'Français' : 
                                         statistics.francoArabeStats.typeBulletin === 'arabe' ? 'Arabe' : 'Complet'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className="mt-4">
                        <h4 className="font-semibold text-gray-700 mb-3">Distribution des Mentions</h4>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                                <p className="text-sm text-green-700 font-medium">Excellent</p>
                                <p className="text-xl font-bold text-green-800">{statistics.distribution.excellent}</p>
                                <p className="text-xs text-green-600">({((statistics.distribution.excellent / statistics.total) * 100).toFixed(1)}%)</p>
                            </div>
                            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-sm text-blue-700 font-medium">Bien</p>
                                <p className="text-xl font-bold text-blue-800">{statistics.distribution.bien}</p>
                                <p className="text-xs text-blue-600">({((statistics.distribution.bien / statistics.total) * 100).toFixed(1)}%)</p>
                            </div>
                            <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                <p className="text-sm text-yellow-700 font-medium">Assez Bien</p>
                                <p className="text-xl font-bold text-yellow-800">{statistics.distribution.assezBien}</p>
                                <p className="text-xs text-yellow-600">({((statistics.distribution.assezBien / statistics.total) * 100).toFixed(1)}%)</p>
                            </div>
                            <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                                <p className="text-sm text-orange-700 font-medium">Passable</p>
                                <p className="text-xl font-bold text-orange-800">{statistics.distribution.passable}</p>
                                <p className="text-xs text-orange-600">({((statistics.distribution.passable / statistics.total) * 100).toFixed(1)}%)</p>
                            </div>
                            <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                                <p className="text-sm text-red-700 font-medium">Insuffisant</p>
                                <p className="text-xl font-bold text-red-800">{statistics.distribution.insuffisant}</p>
                                <p className="text-xs text-red-600">({((statistics.distribution.insuffisant / statistics.total) * 100).toFixed(1)}%)</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Section Statistiques Spécifiques Primaire */}
                    {statistics.isPrimaire && statistics.primaireStats && (
                        <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                            <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                                📊 Statistiques Spécifiques Primaire
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="text-center p-3 bg-white rounded-lg border border-green-100">
                                    <p className="text-sm text-green-700 font-medium">Élèves Primaire</p>
                                    <p className="text-xl font-bold text-green-800">{statistics.primaireStats.totalPrimaire}</p>
                                </div>
                                
                                {statistics.periode === '3ème Trimestre' && (
                                    <div className="text-center p-3 bg-white rounded-lg border border-green-100">
                                        <p className="text-sm text-green-700 font-medium">Moy. Annuelle Calculée</p>
                                        <p className="text-xl font-bold text-green-800">{statistics.primaireStats.avecMoyenneAnnuelle}</p>
                                        <p className="text-xs text-green-600">({((statistics.primaireStats.avecMoyenneAnnuelle / statistics.total) * 100).toFixed(1)}%)</p>
                                    </div>
                                )}
                                
                                <div className="text-center p-3 bg-white rounded-lg border border-green-100">
                                    <p className="text-sm text-green-700 font-medium">Trim 1</p>
                                    <p className="text-xl font-bold text-green-800">{statistics.primaireStats.moyennesTrimestres.trim1}</p>
                                    <p className="text-xs text-green-600">élèves</p>
                                </div>
                                
                                <div className="text-center p-3 bg-white rounded-lg border border-green-100">
                                    <p className="text-sm text-green-700 font-medium">Trim 2</p>
                                    <p className="text-xl font-bold text-green-800">{statistics.primaireStats.moyennesTrimestres.trim2}</p>
                                    <p className="text-xs text-green-600">élèves</p>
                                </div>
                            </div>
                            
                            {statistics.periode === '3ème Trimestre' && (
                                <div className="mt-3 pt-3 border-t border-green-200">
                                    <p className="text-sm text-green-700">
                                        <strong>Note :</strong> Les statistiques utilisent la moyenne annuelle pour le 3ème trimestre primaire
                                        lorsque disponible ({statistics.primaireStats.avecMoyenneAnnuelle} élève(s)).
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {viewMode === 'individual' && generatedBulletin && (
                <>
                    <div className="mb-4 p-4 bg-blue-50 text-blue-800 border border-blue-200 rounded">
                        <p className="font-bold">⚠️ PRÉVISUALISATION À 75%</p>
                        <p className="text-sm mt-1">Le bulletin est affiché à 75% pour s'adapter à l'écran. Le PDF sera généré à 100% pour une qualité optimale.</p>
                        {generatedBulletin.isPrimaire && (
                            <p className="text-sm mt-2 font-semibold">
                                📋 <strong>Mode Primaire/Préscolaire</strong> : Affichage des 3 trimestres côte à côte
                                {generatedBulletin.periode === '3ème Trimestre' && generatedBulletin.moyenneAnnuelle && (
                                <span className="block text-green-700 mt-1">📊 Moyenne Annuelle calculée: {generatedBulletin.moyenneAnnuelle.toFixed(2)} / {generatedBulletin.baseNotation || (generatedBulletin.isPrimaire ? 10 : 20)}</span>
                                )}
                            </p>
                        )}
                        {schoolSettings?.isFrancoArabe && (
                            <div className="mt-3 flex space-x-4">
                                <button 
                                    onClick={() => setSelectedBulletinType('tous')}
                                    className={`px-4 py-2 rounded ${selectedBulletinType === 'tous' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                >
                                    Bulletin Complet
                                </button>
                                <button 
                                    onClick={() => setSelectedBulletinType('français')}
                                    className={`px-4 py-2 rounded ${selectedBulletinType === 'français' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                >
                                    Section Française
                                </button>
                                <button 
                                    onClick={() => setSelectedBulletinType('arabe')}
                                    className={`px-4 py-2 rounded ${selectedBulletinType === 'arabe' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}
                                >
                                    Section Arabe
                                </button>
                            </div>
                        )}
                    </div>
                    
                    {/* Affichage selon le type sélectionné */}
                    {selectedBulletinType === 'tous' && (
                        <BulletinDisplay 
                            bulletin={generatedBulletin} 
                            appreciationText={appreciationText} 
                            setAppreciationText={setAppreciationText} 
                            id="bulletin-to-print" 
                            isPreview={true} 
                            bulletinType="tous"
                        />
                    )}
                    
                    {selectedBulletinType === 'français' && frenchBulletin && (
                        <BulletinDisplay 
                            bulletin={frenchBulletin} 
                            appreciationText={frenchAppreciationText} 
                            setAppreciationText={setFrenchAppreciationText} 
                            id="french-bulletin" 
                            isPreview={true} 
                            bulletinType="français"
                        />
                    )}
                    
                    {selectedBulletinType === 'arabe' && arabicBulletin && (
                        <BulletinDisplay 
                            bulletin={arabicBulletin} 
                            appreciationText={arabicAppreciationText} 
                            setAppreciationText={setArabicAppreciationText} 
                            id="arabic-bulletin" 
                            isPreview={true} 
                            bulletinType="arabe"
                        />
                    )}
                </>
            )}

            {viewMode === 'class' && bulkBulletins.length > 0 && (
                <div id="bulk-bulletins-container">
                    <div className="mb-4 p-4 bg-blue-50 text-blue-800 border border-blue-200 rounded">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="font-bold">⚠️ PRÉVISUALISATION À 75%</p>
                                <p className="text-sm mt-1">Les bulletins sont affichés à 75% pour s'adapter à l'écran. Le PDF sera généré à 100% pour une qualité optimale.</p>
                                {bulkBulletins[0]?.isPrimaire && (
                                    <p className="text-sm mt-2 font-semibold">
                                        📋 <strong>Mode Primaire/Préscolaire</strong> : Affichage des 3 trimestres côte à côte
                                        {selectedPeriode === '3ème Trimestre' && bulkBulletins[0]?.isPrimaire && (
                                        <span className="block text-green-700 mt-1">📊 Moyenne Annuelle calculée pour tous les élèves</span>
                                        )}
                                    </p>
                                )}
                                {schoolSettings?.isFrancoArabe && (
                                    <p className="text-sm mt-2 font-semibold">
                                        📋 <strong>École Franco-Arabe</strong> : Type de bulletin: {selectedBulletinType === 'français' ? 'Section Française' : selectedBulletinType === 'arabe' ? 'Section Arabe' : 'Section Complète'}
                                    </p>
                                )}
                            </div>
                            <div className="text-sm text-blue-700 font-medium">{bulkBulletins.length} bulletin(s) généré(s)</div>
                        </div>
                    </div>
                    {bulkBulletins.map((bull, index) => (
                        <div key={bull.eleveId} className={index > 0 ? 'mt-8' : ''}>
                            <BulletinDisplay 
                                id={`bulletin-${index}`} 
                                bulletin={bull} 
                                appreciationText={bull.appreciations} 
                                setAppreciationText={() => {}} 
                                isPreview={true} 
                                bulletinType={selectedBulletinType}
                            />
                            {index < bulkBulletins.length - 1 && <div className="page-break"></div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BulletinsManagement;